/**
 * A página do telefone confirmado — spec 0071 (C-01, C-03, C-07, FR-004 a FR-009).
 *
 * Em ordem de urgência: o pedido do motorista que espera resposta; o dia de cada
 * criança do telefone, com a falta; os avisos no WhatsApp. Etapa e horários, sem
 * posição, e nenhuma outra criança da van (DD-03).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { type Chamar, Escolha, ErroDaPagina, diaCurto, hojeBRT, hora } from './familia-comum';
import { DecisaoDoPedido, type PedidoDaFamilia } from './pedido-na-hora';

export interface CriancaDoTelefone {
  idPassageiro: number;
  nome: string;
  escola: string | null;
}

interface Perna {
  perna: 'ida' | 'volta';
  haViagem: boolean;
  motivo: string | null;
  saida: string | null;
  etapa: string | null;
  embarcouEm: string | null;
  chegouEm: string | null;
  proximo: boolean;
  idAusencia: number | null;
}

interface Ausencia {
  idAusencia: number;
  idPassageiro: number;
  data: string;
  perna: 'ida' | 'volta' | 'ambas';
  origem: string;
  podeDesfazer: boolean;
}

interface Viagem {
  idViagem: number;
  data: string;
  perna: 'ida' | 'volta';
  resultado: string;
  embarcouEm: string | null;
  chegouEm: string | null;
}

type Sinal = 'andamento' | 'ok' | 'atencao' | 'neutro';

/** A etapa em palavras. O sinal repete a frase em cor, nunca a substitui. */
function etapaDe(p: Perna): { frase: string; sinal: Sinal } {
  const volta = p.perna === 'volta';
  switch (p.etapa) {
    case 'a_van_nao_saiu': return { frase: p.saida ? `A van ainda não saiu. Saída prevista às ${p.saida}` : 'A van ainda não saiu', sinal: 'neutro' };
    case 'aguardando_saida_da_escola': return { frase: 'Na escola, esperando a saída', sinal: 'neutro' };
    case 'a_van_saiu': return { frase: 'A van saiu e está a caminho', sinal: 'andamento' };
    case 'voces_sao_os_proximos': return { frase: volta ? 'Na van. Vocês são os próximos' : 'Vocês são os próximos', sinal: 'andamento' };
    case 'a_van_chegou': return { frase: 'A van chegou ao ponto', sinal: 'andamento' };
    case 'a_caminho_da_escola': return { frase: 'Na van, a caminho da escola', sinal: 'andamento' };
    case 'a_caminho_de_casa': return { frase: 'Na van, a caminho de casa', sinal: 'andamento' };
    case 'continua_na_van': return { frase: 'Continua na van. A cooperativa está acompanhando', sinal: 'atencao' };
    case 'nao_embarcou': return { frase: 'Não embarcou', sinal: 'atencao' };
    case 'chegou_a_escola': return { frase: 'Chegou à escola', sinal: 'ok' };
    case 'retirado_na_escola': return { frase: 'Retirado na escola', sinal: 'ok' };
    case 'entregue': return { frase: 'Entregue', sinal: 'ok' };
    case 'avisou_falta': return { frase: 'Falta avisada: não vai nesta perna', sinal: 'neutro' };
    case 'fora_da_viagem': return { frase: 'Não está na lista desta viagem', sinal: 'neutro' };
    case 'sem_viagem': return { frase: semViagem(p.motivo), sinal: 'neutro' };
    default: return p.motivo === 'sem_rota'
      ? { frase: 'Não está em nenhuma rota nesta perna', sinal: 'neutro' }
      : { frase: 'Viagem registrada', sinal: 'neutro' };
  }
}

function semViagem(motivo: string | null): string {
  if (motivo === 'sem_aula') return 'Sem aula hoje, sem viagem';
  if (motivo === 'sem_passageiro_no_dia') return 'Não tem viagem nesta perna hoje';
  return 'Sem viagem hoje';
}

function horarios(p: Perna): string | null {
  const partes: string[] = [];
  if (p.embarcouEm) partes.push(`embarcou às ${hora(p.embarcouEm)}`);
  if (p.chegouEm) partes.push(`${p.perna === 'ida' ? 'chegou' : 'entregue'} às ${hora(p.chegouEm)}`);
  if (!partes.length) return null;
  const t = partes.join(' · ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

const RESULTADO: Record<string, string> = {
  na_escola: 'Chegou à escola', entregue: 'Entregue', retirado: 'Retirado na escola', faltou: 'Não embarcou',
  avisou_falta: 'Falta avisada', pendente: 'Sem marcação', a_bordo: 'Terminou a bordo', no_ponto: 'Sem marcação',
};

const PERNA: Record<string, string> = { ida: 'Ida', volta: 'Volta', ambas: 'Ida e volta' };

function quemAvisou(origem: string): string {
  if (origem === 'motorista') return 'avisada pelo motorista';
  if (origem === 'painel') return 'avisada pela cooperativa';
  return 'avisada por você';
}

export const DiaDaFamilia: React.FC<{ chamar: Chamar; criancas: CriancaDoTelefone[] }> = ({ chamar, criancas }) => {
  const [pedidos, setPedidos] = useState<PedidoDaFamilia[]>([]);
  const [ausencias, setAusencias] = useState<Ausencia[]>([]);
  const [versao, setVersao] = useState(0);

  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  useEffect(() => {
    // União com os já mostrados: o pedido respondido some da lista do servidor, mas a
    // resposta continua à vista até a pessoa sair da página.
    chamar<PedidoDaFamilia[]>('/pedidos')
      .then((novos) => setPedidos((antes) => [...antes.filter((a) => !novos.some((n) => n.idPedido === a.idPedido)), ...novos]))
      .catch(() => undefined);
    chamar<Ausencia[]>('/ausencias').then(setAusencias).catch(() => undefined);
  }, [chamar, versao]);

  // O dia muda sozinho: a página se atualiza a cada minuto enquanto está à vista.
  useEffect(() => {
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') recarregar(); }, 60_000);
    const aoVoltar = () => { if (document.visibilityState === 'visible') recarregar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', aoVoltar); };
  }, [recarregar]);

  return (
    <>
      {pedidos.length > 0 && (
        <div className="fam-pedidos">
          <h2 className="fam-secao">Pedido do motorista</h2>
          {pedidos.map((p) => (
            <DecisaoDoPedido key={p.idPedido} pedido={p}
              decidir={(autoriza) => chamar<{ status: string }>(`/pedidos/${p.idPedido}/decidir`, { method: 'POST', body: JSON.stringify({ autoriza }) })} />
          ))}
        </div>
      )}

      {criancas.map((c) => (
        <Crianca key={c.idPassageiro} chamar={chamar} crianca={c} versao={versao} onMudou={recarregar}
          ausencias={ausencias.filter((a) => a.idPassageiro === c.idPassageiro)} />
      ))}

      <Confirmacoes chamar={chamar} />
    </>
  );
};

const Crianca: React.FC<{
  chamar: Chamar; crianca: CriancaDoTelefone; versao: number; ausencias: Ausencia[]; onMudou: () => void;
}> = ({ chamar, crianca, versao, ausencias, onMudou }) => {
  const [pernas, setPernas] = useState<Perna[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [avisando, setAvisando] = useState(false);
  const [resultado, setResultado] = useState<{ tipo: 'ok' | 'aviso'; texto: string } | null>(null);
  const [historico, setHistorico] = useState<Viagem[] | null>(null);

  useEffect(() => {
    chamar<Perna[]>(`/dia?idPassageiro=${crianca.idPassageiro}`)
      .then((p) => { setPernas(p); setErro(null); })
      .catch((e) => setErro((e as Error).message));
  }, [chamar, crianca.idPassageiro, versao]);

  const desfazer = async (a: Ausencia) => {
    setResultado(null);
    try {
      await chamar(`/ausencias/${a.idAusencia}/cancelar`, { method: 'POST' });
      setResultado({ tipo: 'ok', texto: `Falta desfeita: ${crianca.nome} volta à lista de ${diaCurto(a.data)}, ${PERNA[a.perna].toLowerCase()}.` });
      onMudou();
    } catch (e) {
      setResultado({ tipo: 'aviso', texto: (e as Error).message });
    }
  };

  const abrirHistorico = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    if (e.currentTarget.open && historico === null) {
      chamar<Viagem[]>(`/historico?idPassageiro=${crianca.idPassageiro}`).then(setHistorico).catch(() => setHistorico([]));
    }
  };

  return (
    <section className="fam-crianca" aria-labelledby={`fam-crianca-${crianca.idPassageiro}`}>
      <h2 id={`fam-crianca-${crianca.idPassageiro}`} className="fam-crianca-nome">
        {crianca.nome}{crianca.escola && <span className="fam-crianca-escola">{crianca.escola}</span>}
      </h2>

      <p className="fam-secao">Hoje, {diaCurto(hojeBRT())}</p>
      {erro ? (
        <p className="fam-erro" role="alert">{erro}</p>
      ) : !pernas ? (
        <p className="fam-apoio" role="status">Carregando…</p>
      ) : (
        <ol className="fam-pernas">
          {pernas.map((p) => {
            const { frase, sinal } = etapaDe(p);
            const h = horarios(p);
            return (
              <li key={p.perna} className="fam-perna">
                <span className={`fam-sinal fam-sinal-${sinal}`} aria-hidden="true" />
                <span className="fam-perna-nome">{PERNA[p.perna]}</span>
                <span className="fam-perna-frase">{frase}</span>
                {h && <span className="fam-perna-hora">{h}</span>}
              </li>
            );
          })}
        </ol>
      )}

      {ausencias.length > 0 && (
        <ul className="fam-faltas" aria-label={`Faltas avisadas de ${crianca.nome}`}>
          {ausencias.map((a) => (
            <li key={a.idAusencia} className="fam-falta">
              <span>
                <span className="fam-falta-dia">Não vai · {diaCurto(a.data)} · {PERNA[a.perna].toLowerCase()}</span>
                <span className="fam-falta-quem">{quemAvisou(a.origem)}</span>
              </span>
              {a.podeDesfazer && (
                <button type="button" className="fam-btn fam-btn-texto" onClick={() => desfazer(a)}
                  aria-label={`Desfazer a falta de ${diaCurto(a.data)}, ${PERNA[a.perna].toLowerCase()}`}>
                  Desfazer
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {resultado && <p className={resultado.tipo === 'ok' ? 'fam-ok' : 'fam-aviso'} role="status">{resultado.texto}</p>}

      {avisando ? (
        <AvisarFalta chamar={chamar} crianca={crianca}
          onPronto={(r) => { setAvisando(false); setResultado(r); onMudou(); }}
          onVoltar={() => setAvisando(false)} />
      ) : (
        <button type="button" className="fam-btn fam-btn-secundario" onClick={() => { setResultado(null); setAvisando(true); }}>
          Avisar que {crianca.nome} não vai
        </button>
      )}

      <details className="fam-historico" onToggle={abrirHistorico}>
        <summary>Últimas viagens</summary>
        {historico === null ? (
          <p className="fam-apoio" role="status">Carregando…</p>
        ) : historico.length === 0 ? (
          <p className="fam-apoio">Nenhuma viagem encerrada nos últimos 30 dias.</p>
        ) : (
          <table className="fam-tabela">
            <caption className="fam-apoio">Viagens encerradas nos últimos 30 dias, com os horários registrados pelo motorista.</caption>
            <thead><tr><th scope="col">Viagem</th><th scope="col">Resultado</th><th scope="col">Horários</th></tr></thead>
            <tbody>
              {historico.map((v) => (
                <tr key={v.idViagem}>
                  <td className="fam-num">{diaCurto(v.data)}<span className="fam-tabela-perna">{PERNA[v.perna]}</span></td>
                  <td>{RESULTADO[v.resultado] ?? 'Registrado'}</td>
                  <td className="fam-num">{[v.embarcouEm && hora(v.embarcouEm), v.chegouEm && hora(v.chegouEm)].filter(Boolean).join(' – ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </details>
    </section>
  );
};

type QualDia = 'hoje' | 'amanha' | 'outro';
type QualPerna = 'ida' | 'volta' | 'ambas';

const AvisarFalta: React.FC<{
  chamar: Chamar; crianca: CriancaDoTelefone;
  onPronto: (r: { tipo: 'ok' | 'aviso'; texto: string }) => void; onVoltar: () => void;
}> = ({ chamar, crianca, onPronto, onVoltar }) => {
  const [dia, setDia] = useState<QualDia>('hoje');
  const [outro, setOutro] = useState('');
  const [perna, setPerna] = useState<QualPerna>('ambas');
  const [erroData, setErroData] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const idBase = `fam-falta-${crianca.idPassageiro}`;

  useEffect(() => { document.getElementById(`${idBase}-titulo`)?.focus(); }, [idBase]);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    const data = dia === 'hoje' ? hojeBRT() : dia === 'amanha' ? hojeBRT(1) : outro;
    if (dia === 'outro' && (!outro || outro < hojeBRT())) {
      setErroData('Escolha um dia de hoje em diante.');
      document.getElementById(`${idBase}-data`)?.focus();
      return;
    }
    setErroData(null);
    setOcupado(true);
    try {
      const r = await chamar<{ saiuDaChamada: boolean; vanJaPassou: boolean }>('/ausencias', {
        method: 'POST', body: JSON.stringify({ idPassageiro: crianca.idPassageiro, data, perna }),
      });
      const quando = `${diaCurto(data)}, ${PERNA[perna].toLowerCase()}`;
      onPronto(r.vanJaPassou
        ? { tipo: 'aviso', texto: `A van já passou pelo seu ponto nesta perna. A falta de ${quando} ficou registrada; se precisar, fale com o motorista.` }
        : { tipo: 'ok', texto: `Pronto: o motorista vai saber que ${crianca.nome} não vai ${quando}.` });
    } catch (err) {
      setErro(err instanceof ErroDaPagina && err.codigo === 'AUSENCIA_DUPLICADA'
        ? 'Já há uma falta avisada para esse dia. Desfaça a anterior se quiser mudar a perna.'
        : (err as Error).message);
      setOcupado(false);
    }
  };

  return (
    <form className="fam-form fam-falta-form" onSubmit={enviar} noValidate>
      <h3 id={`${idBase}-titulo`} className="fam-subtitulo" tabIndex={-1}>{crianca.nome} não vai</h3>
      <Escolha nome={`${idBase}-dia`} legenda="Quando" valor={dia} onChange={setDia} desabilitado={ocupado}
        opcoes={[{ valor: 'hoje', rotulo: 'Hoje' }, { valor: 'amanha', rotulo: 'Amanhã' }, { valor: 'outro', rotulo: 'Outro dia' }]} />
      {dia === 'outro' && (
        <div className="fam-campo">
          <label className="fam-rotulo" htmlFor={`${idBase}-data`}>Dia</label>
          <input id={`${idBase}-data`} type="date" min={hojeBRT()} value={outro} onChange={(e) => setOutro(e.target.value)}
            aria-invalid={Boolean(erroData)} aria-describedby={erroData ? `${idBase}-data-erro` : undefined} />
          {erroData && <small id={`${idBase}-data-erro`} className="fam-erro-campo">{erroData}</small>}
        </div>
      )}
      <Escolha nome={`${idBase}-perna`} legenda="Em qual viagem" valor={perna} onChange={setPerna} desabilitado={ocupado}
        opcoes={[{ valor: 'ida', rotulo: 'Ida' }, { valor: 'volta', rotulo: 'Volta' }, { valor: 'ambas', rotulo: 'As duas' }]} />
      {erro && <p className="fam-erro" role="alert">{erro}</p>}
      <div className="fam-acoes">
        <button type="button" className="fam-btn fam-btn-texto" onClick={onVoltar} disabled={ocupado}>Voltar</button>
        <button type="submit" className="fam-btn fam-btn-primario" disabled={ocupado}>{ocupado ? 'Enviando…' : 'Avisar a falta'}</button>
      </div>
    </form>
  );
};

/** FR-009: o essencial sempre chega; as confirmações (embarcou, chegou) são escolha da família. */
const Confirmacoes: React.FC<{ chamar: Chamar }> = ({ chamar }) => {
  const [valor, setValor] = useState<'essencial' | 'tudo' | null>(null);
  const [estado, setEstado] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    chamar<{ confirmacao: boolean }>('/preferencias').then((p) => setValor(p.confirmacao ? 'tudo' : 'essencial')).catch(() => setValor('essencial'));
  }, [chamar]);

  const mudar = async (v: 'essencial' | 'tudo') => {
    const antes = valor;
    setValor(v);
    setEstado(null);
    setOcupado(true);
    try {
      await chamar('/preferencias', { method: 'PUT', body: JSON.stringify({ confirmacao: v === 'tudo' }) });
      setEstado({ tipo: 'ok', texto: 'Salvo.' });
    } catch (e) {
      setValor(antes);
      setEstado({ tipo: 'erro', texto: (e as Error).message });
    } finally {
      setOcupado(false);
    }
  };

  if (valor === null) return null;
  return (
    <section className="fam-bloco" aria-labelledby="fam-avisos-titulo">
      <h2 id="fam-avisos-titulo" className="fam-secao">Avisos no WhatsApp</h2>
      <p className="fam-apoio">
        O essencial sempre chega: a saída da van, "vocês são os próximos" e qualquer imprevisto.
      </p>
      <Escolha nome="fam-avisos" legenda="O que receber" valor={valor} onChange={mudar} desabilitado={ocupado}
        opcoes={[{ valor: 'essencial', rotulo: 'Só o essencial' }, { valor: 'tudo', rotulo: 'Embarque e chegada' }]} />
      {estado && <p className={estado.tipo === 'ok' ? 'fam-salvo' : 'fam-erro'} role="status">{estado.texto}</p>}
    </section>
  );
};
